import type {ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import type {InstagramAudioTrack} from '@Components/social-media-wizard/social-media-wizard.types';
import {ReelMusicSearchState} from '../reel-music-search/reel-music-search.types';
import {ReelMusicTrackItem} from '../reel-music-track-item/reel-music-track-item';
import {ReelMusicTrackListSkeleton} from '../reel-music-track-list-skeleton/reel-music-track-list-skeleton';
import type {ReelMusicTrackListProps} from './reel-music-track-list.types';
import styles from './reel-music-track-list.module.scss';

export function ReelMusicTrackList({tracks, searchState, selectedAudioId, onSelectTrack, onRetry}: ReelMusicTrackListProps): ReactElement {
  const getTrack = (track: InstagramAudioTrack): ReactElement => {
    return <ReelMusicTrackItem key={track.audioId} track={track} isSelected={track.audioId === selectedAudioId} onSelectTrack={onSelectTrack} />;
  };

  if (searchState === ReelMusicSearchState.LOADING) {
    return <ReelMusicTrackListSkeleton />;
  }

  if (searchState === ReelMusicSearchState.ERROR) {
    return (
      <div role="alert" className={`${styles.trackListState} flex-v-row flex-center -text-align-center`}>
        <Icon
          flatIconType={{color: FlatIconColor.DANGER, type: FlatIconColorType.DARK}}
          type={IconType.FLAT}
          shape={IconShape.CIRCLE}
          size={IconSize.SIZE_ICON_20}
          icon="icon-exclamation-triangle"
        />
        <Text val={window.i18next.t('pmwjs_reel_music_load_error')} size={TextSize.SM} className={styles.trackListStateText} />
        <Button icon="icon-rotate-ccw" type={ButtonType.SECONDARY} size={ButtonSize.SMALL} text={window.i18next.t('pmwjs_retry')} onClick={onRetry} />
      </div>
    );
  }

  if (tracks.length === 0) {
    return (
      <div className={`${styles.trackListState} flex-center`}>
        <Text val={window.i18next.t('pmwjs_reel_music_no_results')} size={TextSize.SM} className={styles.trackListStateText} />
      </div>
    );
  }

  return (
    <div role="listbox" aria-label={window.i18next.t('pmwjs_reel_music')} className="flexbox flex-column gap-2 spacing-p-b-2">
      {tracks.map(getTrack)}
    </div>
  );
}
